import React from 'react';

// Update net worth data to 2025 projections
const getMedianNetWorthByAge = (age: number): number => {
  if (age < 35) return 42000;
  if (age < 45) return 98500;
  if (age < 55) return 178900;
  if (age < 65) return 228700;
  if (age < 75) return 284500;
  return 272400;
};

const getMedianNetWorthByIncome = (income: number): number => {
  if (income < 25000) return 13500;
  if (income < 50000) return 52400;
  if (income < 75000) return 96800;
  if (income < 100000) return 189500;
  if (income < 150000) return 372600;
  if (income < 200000) return 668900;
  return 1375000;
};

// Updated location factors based on 2025 cost of living projections
const locationFactors: { [key: string]: number } = {
  'Northeast': 1.25,
  'Midwest': 0.92,
  'South': 0.88,
  'West': 1.18,
  'Alaska/Hawaii': 1.30
};

interface NetWorthComparisonProps {
  age: number;
  income: number;
  netWorth: number;
  location: string;
}

export const NetWorthComparison: React.FC<NetWorthComparisonProps> = ({
  age,
  income,
  netWorth,
  location
}) => {
  const medianByAge = getMedianNetWorthByAge(age);
  const medianByIncome = getMedianNetWorthByIncome(income);
  const locationFactor = locationFactors[location] || 1;
  
  const adjustedMedianByAge = medianByAge * locationFactor;
  const adjustedMedianByIncome = medianByIncome * locationFactor;
  
  const comparisonByAge = ((netWorth / adjustedMedianByAge) * 100).toFixed(1);
  const comparisonByIncome = ((netWorth / adjustedMedianByIncome) * 100).toFixed(1);

  return (
    <div className="space-y-6">
      <div className="glass-card p-6">
        <h4 className="text-lg font-semibold mb-4">Comparison by Age Group</h4>
        <div className="space-y-2">
          <p className="text-sm text-slate-400">
            Median net worth for your age group ({age} years) in {location}:
          </p>
          <p className="text-xl font-semibold">
            ${adjustedMedianByAge.toLocaleString()}
          </p>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full ${
                Number(comparisonByAge) >= 100 ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
              style={{ width: `${Math.min(Number(comparisonByAge), 200)}%` }}
            />
          </div>
          <p className="text-sm text-slate-400">
            Your net worth is {comparisonByAge}% of the median for your age group
          </p>
        </div>
      </div>

      <div className="glass-card p-6">
        <h4 className="text-lg font-semibold mb-4">Comparison by Income Level</h4>
        <div className="space-y-2">
          <p className="text-sm text-slate-400">
            Median net worth for your income level (${income.toLocaleString()}/year) in {location}:
          </p>
          <p className="text-xl font-semibold">
            ${adjustedMedianByIncome.toLocaleString()}
          </p>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full ${
                Number(comparisonByIncome) >= 100 ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
              style={{ width: `${Math.min(Number(comparisonByIncome), 200)}%` }}
            />
          </div>
          <p className="text-sm text-slate-400">
            Your net worth is {comparisonByIncome}% of the median for your income level
          </p>
        </div>
      </div>
    </div>
  );
};